import {
  PartnerDealCommitteeQueueItemResponse,
  PartnerDealCommitteeQueueVoterResponse,
  PartnerDealCommitteeVote,
} from '@interfaces/partners/responses/partner-deal-committee.response';
import {
  CommitteeTag,
  isFinalVote,
} from '@app/features/dashboard/commercial/pages/deal-detail/components/deal-committee-panel/deal-committee-presenter';

/**
 * Textos e contagens da fila Comercial › Comitê de crédito (card 3789). Funções puras, testadas em
 * `credit-committee-presenter.spec.ts`. Quem decide se a pessoa é votante, se a contestação dela está
 * aberta e se o prazo venceu é o servidor (`isVoter`, `myContestOpen`, `isOverdue`).
 */

/** Os quatro números do topo da fila. */
export interface CreditCommitteeQueueKpis {
  awaitingMyVote: number;
  awaitingMyVoteOverdue: number;
  openContests: number;
  overdue: number;
  decided: number;
  approved: number;
  denied: number;
}

/**
 * O deal espera o voto de quem está vendo: é votante, ainda não deu voto final e não está com uma
 * contestação própria aguardando resposta (aí a bola está com o comercial).
 */
export function isAwaitingMyVote(item: PartnerDealCommitteeQueueItemResponse): boolean {
  return item.status === 'Open' && item.isVoter && !isFinalVote(item.myVote) && !item.myContestOpen;
}

/** Números do topo, a partir das duas listas que a tela carrega. */
export function computeQueueKpis(
  open: PartnerDealCommitteeQueueItemResponse[],
  decided: PartnerDealCommitteeQueueItemResponse[]
): CreditCommitteeQueueKpis {
  const mine = open.filter(isAwaitingMyVote);
  return {
    awaitingMyVote: mine.length,
    awaitingMyVoteOverdue: mine.filter(i => i.isOverdue).length,
    openContests: open.reduce((sum, i) => sum + (i.openContestCount ?? 0), 0),
    overdue: open.filter(i => i.isOverdue).length,
    decided: decided.length,
    approved: decided.filter(i => i.status === 'Approved').length,
    denied: decided.filter(i => i.status === 'Denied').length,
  };
}

/**
 * A fila em comitê dividida como a tela mostra (opção A do protótipo, 04/11/2026): o que espera o voto de
 * quem está vendo, no topo, e o resto em votação com os outros.
 */
export function splitOpenQueue(open: PartnerDealCommitteeQueueItemResponse[]): {
  mine: PartnerDealCommitteeQueueItemResponse[];
  others: PartnerDealCommitteeQueueItemResponse[];
} {
  return {
    mine: open.filter(isAwaitingMyVote),
    others: open.filter(i => !isAwaitingMyVote(i)),
  };
}

/** A situação da linha, do ponto de vista de quem está vendo. */
export function describeQueueSituation(item: PartnerDealCommitteeQueueItemResponse): CommitteeTag {
  switch (item.status) {
    case 'Approved':
      return { label: 'Aprovado', tone: 'success' };
    case 'Denied':
      return { label: 'Negado', tone: 'danger' };
    case 'Cancelled':
      return { label: 'Cancelado', tone: 'neutral' };
  }

  if (item.isVoter && item.myContestOpen) {
    return { label: 'Sua contestação está em aberto', tone: 'warning' };
  }
  if (item.isVoter && item.myVote === 'Approve') return { label: 'Você aprovou', tone: 'success' };
  if (item.isVoter && item.myVote === 'Deny') return { label: 'Você negou', tone: 'danger' };
  if (item.isOverdue) return { label: 'Prazo vencido', tone: 'danger' };
  if (item.isVoter) return { label: 'Aguardando seu voto', tone: 'neutral' };
  if (item.openContestCount > 0) return { label: 'Contestação em aberto', tone: 'warning' };
  return { label: 'Em votação', tone: 'info' };
}

/** Tom do selo de um votante no placar. */
export type VoterTone = 'approved' | 'denied' | 'contested' | 'pending';

/** Iniciais do votante para o selo: primeira letra do primeiro e do último nome ("Ana Lima" → "AL"). */
export function voterInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] ?? '');
  return (first + last).toUpperCase();
}

/** Tom do selo pelo voto. */
export function voterTone(vote: PartnerDealCommitteeVote | null): VoterTone {
  switch (vote) {
    case 'Approve':
      return 'approved';
    case 'Deny':
      return 'denied';
    case 'Contest':
      return 'contested';
    default:
      return 'pending';
  }
}

/** Texto do selo ao passar o mouse e para leitor de tela: "Ana Lima aprovou", "Você ainda não votou". */
export function describeVoterVote(voter: PartnerDealCommitteeQueueVoterResponse): string {
  const who = voter.isMe ? 'Você' : voter.name;
  switch (voter.vote) {
    case 'Approve':
      return `${who} aprovou`;
    case 'Deny':
      return `${who} negou`;
    case 'Contest':
      return `${who} contestou`;
    default:
      return `${who} ainda não votou`;
  }
}

/**
 * Resumo do placar ao lado dos selos: "nenhum voto · 2 votos decidem", "1 aprovação · seu voto pode decidir".
 * "Pode decidir" só quando falta um voto para a maioria e quem vê ainda deve o voto.
 */
export function describeScore(item: PartnerDealCommitteeQueueItemResponse): string {
  const parts: string[] = [];
  if (item.approvals > 0) {
    parts.push(`${item.approvals} ${item.approvals === 1 ? 'aprovação' : 'aprovações'}`);
  }
  if (item.denials > 0) {
    parts.push(`${item.denials} ${item.denials === 1 ? 'negação' : 'negações'}`);
  }
  const votes = parts.length ? parts.join(' · ') : 'nenhum voto';
  const oneShort =
    item.majority > 0 &&
    (item.approvals === item.majority - 1 || item.denials === item.majority - 1);
  if (isAwaitingMyVote(item) && oneShort) return `${votes} · seu voto pode decidir`;
  return `${votes} · ${item.majority} ${item.majority === 1 ? 'voto decide' : 'votos decidem'}`;
}

/**
 * Prazo relativo: "vence em 1 dia e 4 h", "vence em 35 min", "venceu há 2 h". Recebe os instantes em ms
 * (o componente converte pelo `DateFormatterService`); quem diz se venceu de verdade é o `isOverdue` do servidor.
 */
export function describeDeadline(
  deadlineMs: number,
  nowMs: number
): { label: string; overdue: boolean } {
  const diff = deadlineMs - nowMs;
  const span = formatSpan(Math.abs(diff));
  return diff >= 0
    ? { label: `vence em ${span}`, overdue: false }
    : { label: `venceu há ${span}`, overdue: true };
}

/** Duração legível, sem segundos: "1 dia e 4 h", "3 h", "35 min", "menos de 1 min". */
function formatSpan(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'menos de 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  const dayText = `${days} ${days === 1 ? 'dia' : 'dias'}`;
  return rest ? `${dayText} e ${rest} h` : dayText;
}

/** Fatia de aprovados na barra dos decididos, em % (0 quando não há decididos). */
export function approvedShare(approved: number, decided: number): number {
  return decided > 0 ? Math.round((approved / decided) * 100) : 0;
}
